import { useRef, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@clerk/clerk-react';
import { Link2, Plus, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/shared/components/ui/Dialog';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import {
  useIntegrations,
  useSyncIntegration,
} from '@/shared/hooks/useIntegrations';
import { useNangoConnect } from '@/shared/hooks/useNangoConnect';
import { useImportMsProject } from '@/shared/hooks/useIssues';
import { IntegrationProvider, Role } from '@momentum/types';
import type { ImportMsProjectApiResponse } from '@momentum/types';
import {
  JiraLogo,
  LinearLogo,
  MondayLogo,
  SalesforceLogo,
  MsProjectLogo,
  BambooHRLogo,
  SharePointLogo,
  DynamicsCrmLogo,
  ConfluenceLogo,
} from './components/integration-logos';
import { ConnectionsTab } from './components/ConnectionsTab';
import { BrowseTab } from './components/BrowseTab';
import { AiConnectionsTab } from './components/AiConnectionsTab';
import { ImportResultDialog } from './components/ImportResultDialog';
import { CrmConnectDialog } from './components/CrmConnectDialog';
import type { BrowseIntegration } from './types';

type Tab = 'connections' | 'browse' | 'ai-connections';

export const IntegrationsDialog = () => {
  const { t } = useTranslation('goals');
  const { orgRole } = useAuth();
  const currentEmployee = useEmployeeContext();
  const companyId = currentEmployee.companyId;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isDev = import.meta.env.VITE_ENVIRONMENT === 'development';
  const canDelete = orgRole === Role.system_admin || isDev;

  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('connections');
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [showMicrosoftCrmDialog, setShowMicrosoftCrmDialog] = useState(false);
  const [importResult, setImportResult] =
    useState<ImportMsProjectApiResponse | null>(null);
  const { data: integrations, isLoading } = useIntegrations(companyId);
  const syncIntegrationMutation = useSyncIntegration();
  const importMutation = useImportMsProject();

  const { connect } = useNangoConnect({
    successMessage: t('integrations.connectIntegration'),
    onOpen: () => setOpen(false),
  });

  const hasIntegrations = integrations && integrations.length > 0;

  const jiraConnected =
    hasIntegrations &&
    integrations.some(
      (integration) => integration.provider === IntegrationProvider.JIRA
    );

  const sharePointConnected =
    hasIntegrations &&
    integrations.some(
      (integration) => integration.provider === IntegrationProvider.SHAREPOINT
    );

  const dynamicsCrmConnected =
    hasIntegrations &&
    integrations.some(
      (integration) => integration.provider === IntegrationProvider.DYNAMICS_CRM
    );

  const confluenceConnected =
    hasIntegrations &&
    integrations.some(
      (integration) => integration.provider === IntegrationProvider.CONFLUENCE
    );

  const handleSync = async (integrationId: string, provider: string) => {
    try {
      const result = await syncIntegrationMutation.mutateAsync({
        companyId,
        integrationId,
      });
      toast.success(
        t('integrations.syncSuccess')(result.totalIssues, provider)
      );
    } catch {
      toast.error(t('integrations.syncFailed')(provider));
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xml')) {
      alert(t('importError.invalidFileType'));
      return;
    }

    try {
      const result = await importMutation.mutateAsync(file);
      setImportResult(result);
      setOpen(false);
      setShowResultDialog(true);
    } catch (error) {
      console.error('Import failed:', error);
      setImportResult({
        goalsCreated: 0,
        tasksCreated: 0,
        totalItems: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
      });
      setOpen(false);
      setShowResultDialog(true);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleMicrosoftCrmConnect = (crmOrgUrl: string) => {
    setShowMicrosoftCrmDialog(false);
    connect({
      crmOrgUrl,
      providerConfigKey: 'microsoft-tenant-specific',
    });
  };

  const handleCloseResultDialog = () => {
    setShowResultDialog(false);
    setImportResult(null);
  };

  const browseIntegrations: BrowseIntegration[] = useMemo(
    () => [
      {
        id: 'jira',
        nameKey: 'integrationsDialog.jira',
        descriptionKey: 'integrationsDialog.jiraDescription',
        logo: <JiraLogo />,
        isInstalled: !!jiraConnected,
        onClick: () => connect({ providerConfigKey: 'jira' }),
      },
      {
        id: 'sharepoint',
        nameKey: 'integrationsDialog.sharepoint',
        descriptionKey: 'integrationsDialog.sharepointDescription',
        logo: <SharePointLogo />,
        isInstalled: !!sharePointConnected,
        onClick: () => connect({ providerConfigKey: 'sharepoint-online' }),
      },
      {
        id: 'dynamics-crm',
        nameKey: 'integrationsDialog.dynamicsCrm',
        descriptionKey: 'integrationsDialog.dynamicsCrmDescription',
        logo: <DynamicsCrmLogo />,
        isInstalled: !!dynamicsCrmConnected,
        onClick: () => setShowMicrosoftCrmDialog(true),
      },
      {
        id: 'confluence',
        nameKey: 'integrationsDialog.confluence',
        descriptionKey: 'integrationsDialog.confluenceDescription',
        logo: <ConfluenceLogo />,
        isInstalled: !!confluenceConnected,
        onClick: () => connect({ providerConfigKey: 'confluence' }),
      },
      {
        id: 'linear',
        nameKey: 'integrationsDialog.linear',
        descriptionKey: 'integrationsDialog.linearDescription',
        logo: <LinearLogo />,
        comingSoon: true,
      },
      {
        id: 'monday',
        nameKey: 'integrationsDialog.monday',
        descriptionKey: 'integrationsDialog.mondayDescription',
        logo: <MondayLogo />,
        comingSoon: true,
      },
      {
        id: 'salesforce',
        nameKey: 'integrationsDialog.salesforce',
        descriptionKey: 'integrationsDialog.salesforceDescription',
        logo: <SalesforceLogo />,
        comingSoon: true,
      },
      {
        id: 'msproject',
        nameKey: 'msProjectTitle',
        descriptionKey: 'integrationsDialog.syncPlansAndTimelines',
        logo: <MsProjectLogo />,
        onClick: () => fileInputRef.current?.click(),
      },
      {
        id: 'bamboohr',
        nameKey: 'integrationsDialog.bamboohr',
        descriptionKey: 'integrationsDialog.bamboohrDescription',
        logo: <BambooHRLogo />,
        comingSoon: true,
      },
    ],
    [
      jiraConnected,
      sharePointConnected,
      dynamicsCrmConnected,
      confluenceConnected,
      connect,
    ]
  );

  return (
    <>
      <Input
        ref={fileInputRef}
        type="file"
        accept=".xml"
        onChange={handleFileChange}
        className="hidden"
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="gap-2 hover:bg-gray-100"
          >
            <Link2 className="h-4 w-4" />
            {t('integrationsDialog.title')}
          </Button>
        </DialogTrigger>
        <DialogContent
          size="lg"
          showCloseButton={false}
          className="!max-w-5xl overflow-hidden p-0"
        >
          <div className="flex h-[600px]">
            {/* ─── Sidebar ─── */}
            <div className="flex w-56 shrink-0 flex-col border-e bg-muted/30">
              <DialogHeader className="px-5 pt-6 pb-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                    <Link2 className="h-5 w-5" />
                  </div>
                  <DialogTitle className="text-base font-bold">
                    {t('integrationsDialog.title')}
                  </DialogTitle>
                </div>
                <DialogDescription className="mt-2 text-sm text-muted-foreground">
                  {t('integrationsDialog.description')}
                </DialogDescription>
              </DialogHeader>

              <nav className="flex flex-col gap-1 px-3">
                <button
                  onClick={() => setActiveTab('connections')}
                  className={`flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2.5 text-start text-sm transition-colors ${
                    activeTab === 'connections'
                      ? 'font-medium text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Link2 className="h-4 w-4 shrink-0" />
                  {t('integrationsDialog.activeConnections')}
                  {hasIntegrations && (
                    <span className="ms-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-bold text-primary-foreground">
                      {integrations.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('browse')}
                  className={`flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2.5 text-start text-sm transition-colors ${
                    activeTab === 'browse'
                      ? 'font-medium text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Plus className="h-4 w-4 shrink-0" />
                  {t('integrationsDialog.addIntegration')}
                </button>
                <button
                  onClick={() => setActiveTab('ai-connections')}
                  className={`flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2.5 text-start text-sm transition-colors ${
                    activeTab === 'ai-connections'
                      ? 'font-medium text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Sparkles className="h-4 w-4 shrink-0" />
                  {t('integrationsDialog.aiConnections')}
                </button>
              </nav>
            </div>

            {/* ─── Main Content ─── */}
            <div
              className="flex flex-1 flex-col overflow-hidden"
              style={{ background: '#f1f2f5' }}
            >
              {isLoading ? (
                <div className="flex flex-1 items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : activeTab === 'connections' ? (
                <ConnectionsTab
                  integrations={integrations ?? []}
                  companyId={companyId}
                  onSync={handleSync}
                  isSyncing={syncIntegrationMutation.isPending}
                  canDelete={canDelete}
                  onBrowse={() => setActiveTab('browse')}
                />
              ) : activeTab === 'ai-connections' ? (
                <AiConnectionsTab />
              ) : (
                <BrowseTab
                  integrations={browseIntegrations}
                  isImporting={importMutation.isPending}
                />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ImportResultDialog
        open={showResultDialog}
        onOpenChange={setShowResultDialog}
        result={importResult}
        onClose={handleCloseResultDialog}
      />

      <CrmConnectDialog
        open={showMicrosoftCrmDialog}
        onOpenChange={setShowMicrosoftCrmDialog}
        onConnect={handleMicrosoftCrmConnect}
        isConnecting={false}
      />
    </>
  );
};
